// PeriodDialog.jsx - Dialog for Creating/Editing KPI Periods

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { createPeriodRequest, updatePeriodRequest } from '../redux.actions';
import { selectLoading } from '../redux.selectors';
import { DATE_FORMAT, VALIDATION_MESSAGES } from '../common';

const PeriodDialog = ({ open, onClose, selectedPeriod }) => {
  const dispatch = useDispatch();
  const loading = useSelector(selectLoading);

  const [formData, setFormData] = useState({
    name: '',
    startDate: null,
    endDate: null,
    description: '',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (selectedPeriod) {
      setFormData({
        name: selectedPeriod.name || '',
        startDate: selectedPeriod.startDate
          ? dayjs(selectedPeriod.startDate)
          : null,
        endDate: selectedPeriod.endDate
          ? dayjs(selectedPeriod.endDate)
          : null,
        description: selectedPeriod.description || '',
      });
    } else {
      setFormData({
        name: '',
        startDate: null,
        endDate: null,
        description: '',
      });
    }
    setErrors({});
  }, [selectedPeriod, open]);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleDateChange = (field) => (newValue) => {
    setFormData((prev) => ({
      ...prev,
      [field]: newValue,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = VALIDATION_MESSAGES.NAME_REQUIRED;
    }

    if (!formData.startDate) {
      newErrors.startDate = VALIDATION_MESSAGES.START_DATE_REQUIRED;
    }

    if (!formData.endDate) {
      newErrors.endDate = VALIDATION_MESSAGES.END_DATE_REQUIRED;
    }

    if (formData.startDate && formData.endDate) {
      const start = dayjs(formData.startDate);
      const end = dayjs(formData.endDate);
      if (end.isBefore(start) || end.isSame(start)) {
        newErrors.endDate = VALIDATION_MESSAGES.INVALID_DATE_RANGE;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const payload = {
      name: formData.name.trim(),
      startDate: formData.startDate ? formData.startDate.format(DATE_FORMAT) : '',
      endDate: formData.endDate ? formData.endDate.format(DATE_FORMAT) : '',
    };

    if (formData.description.trim()) {
      payload.description = formData.description.trim();
    }

    if (selectedPeriod) {
      dispatch(updatePeriodRequest(selectedPeriod.id, payload));
    } else {
      dispatch(createPeriodRequest(payload));
    }

    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {selectedPeriod ? 'Edit KPI Period' : 'Add KPI Period'}
      </DialogTitle>
      <DialogContent dividers sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Period Name"
              value={formData.name}
              onChange={handleChange('name')}
              error={!!errors.name}
              helperText={errors.name}
              required
              fullWidth
              placeholder="e.g., Q1 2025, Annual 2025"
              size="small"
            />

            <DatePicker
              label="Start Date"
              value={formData.startDate}
              onChange={handleDateChange('startDate')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={!!errors.startDate}
                  helperText={errors.startDate}
                  required
                  fullWidth
                  size="small"
                />
              )}
            />

            <DatePicker
              label="End Date"
              value={formData.endDate}
              onChange={handleDateChange('endDate')}
              minDate={formData.startDate || undefined}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={!!errors.endDate}
                  helperText={errors.endDate}
                  required
                  fullWidth
                  size="small"
                />
              )}
            />

            <TextField
              label="Description"
              value={formData.description}
              onChange={handleChange('description')}
              multiline
              rows={3}
              fullWidth
              placeholder="Detailed description of the evaluation period"
              size="small"
            />
          </Box>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleCancel} disabled={loading} size="small">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading && <CircularProgress size={16} />}
          size="small"
        >
          {selectedPeriod ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PeriodDialog;
